// Walled compounds (docs/plans/2026-10-18-no-go.md, 2026-10-18-walls.md, 2026-10-18-street-geometry.md): the plots of
// Lutyens' Delhi nobody may enter. Their walls are vector lines from the map's no-go overlay: each road's right-of-way
// edge, parallel to the road whatever its angle, and the tile edges where a compound meets a driveway or a park. The
// ground is painted with the map (render.ts: lawn behind the walls, the road's pavement up to them).
// Each wall is built from the wall packs, the kind its compound calls for (the observatory's sandstone, government
// railings on a plinth, the police station's barred wall, plain office walls, bungalow brick, garden and yard fences),
// one bay at a time along each segment. A bay is its kit sprite drawn sheared onto the segment: in this oblique view a
// wall at any angle is its front elevation with the base turned along the ground and the height still up the screen, so
// one sprite fits every angle. Under it the wall's body (its cap, and the face towards the viewer) is drawn as a solid,
// so a wall running up the screen, whose face the view sees edge-on, still reads. Piers stand where the overlay puts
// them (at the same distance along the road on both sides, so they face each other), gates where the map has them, and
// a guard booth inside each guarded gate.
import type { GameMap, Wall } from '../shared/map';
import type { Standing } from './render';
import { atlas, standing } from './props';
import SPRITES from './sprites.json';

const K: number = SPRITES.k ?? 0.6; // the oblique view the sprites and buildings share
interface SpriteRect { x: number; y: number; w: number; h: number; ax: number; ay: number; ppm?: number }
const RECTS = SPRITES.sprites as Record<string, SpriteRect>;

/** A kit piece: its sprite key, its length along the wall and where its wall line sits off its origin (metres, life size). */
interface Bay { key: string; lenM: number; zcM: number }
const G = 'kenney-graveyard-kit/', B = 'kenney-building-kit/', P = 'kenney-prototype-kit/';
// measured from the models (scale.json applied): the Graveyard Kit's pieces lie on their 3 m cell's north edge
const bay = (key: string, lenM: number, zcM = 0): Bay => ({ key, lenM, zcM });
/**
 * The wall each kind of compound gets (tools/nogo.py says which kind it is). scale: times life size, so a bay is about a
 * tile long and the wall comes up to a body's shoulder. heightM, thickM: the wall's body, life size. face, cap: its
 * colours where the view sees it edge-on. every: one bay in this many is its run's second look (barred windows, an
 * opening), for rhythm.
 */
interface Style { run: Bay; alt?: Bay; every?: number; pier?: Bay; post?: string; gate: Bay; scale: number; heightM: number; thickM: number; face: string; cap: string; open?: boolean }
export const STYLES: Record<string, Style> = {
  // the observatory: a sandstone boundary wall on piers
  heritage: { run: bay(`${G}stone-wall@0`, 3, -1.2), pier: bay(`${G}stone-wall-column@0`, 3, -1.2), gate: bay(`${G}iron-fence-border-gate@0`, 3, -0.975), scale: 2.5, heightM: 2.17, thickM: 0.6, face: '#A4A3B1', cap: '#D4D3DE' },
  // government sites: iron railings on a stone plinth, between piers
  government: { run: bay(`${G}iron-fence-border@0`, 3, -0.975), pier: bay(`${G}iron-fence-border-column@0`, 3, -0.975), gate: bay(`${G}iron-fence-border-gate@0`, 3, -0.975), scale: 2.5, heightM: 0.5, thickM: 0.45, face: '#8D8E9B', cap: '#3F8A5C', open: true },
  // a bungalow's brick compound wall on piers
  bungalow: { run: bay(`${G}brick-wall@0`, 3, -1.05), pier: bay(`${G}brick-wall-end@0`, 3, -1.03), gate: bay(`${G}iron-fence-border-gate@0`, 3, -0.975), scale: 2.5, heightM: 2.16, thickM: 0.9, face: '#9B9BA9', cap: '#C9CAD4' },
  // the police station: a tall plain wall, barred windows in turn, a sliding steel gate, posts at the ends
  police: { run: bay(`${P}wall@90`, 2.4), alt: bay(`${P}wall-window-barred-medium@90`, 2.4), every: 2, post: `${P}column`, gate: bay(`${P}door-sliding-double@90`, 2.16), scale: 1.5625, heightM: 2.4, thickM: 0.5, face: '#9EA0AA', cap: '#C4C6CE' },
  // office plots: plain rendered walls in bays, now and then a wide opening, a sliding gate
  commercial: { run: bay(`${B}wall@90`, 2), alt: bay(`${B}wall-window-wide-square@90`, 4), every: 3, post: `${B}column`, gate: bay(`${P}door-sliding-double-wide@90`, 3.36), scale: 1.875, heightM: 2.4, thickM: 0.2, face: '#B4B5C0', cap: '#D9DAE2' },
  // a garden: a low picket fence
  garden: { run: bay('kenney-city-kit-suburban/fence@0', 1.66), gate: bay(`${G}fence-gate@0`, 3), scale: 1.5, heightM: 0.95, thickM: 0.1, face: '#8A6A4A', cap: '#A88560', open: true },
  // a back strip too small to be a plot: a wooden fence
  yard: { run: bay(`${G}fence@0`, 3), gate: bay(`${G}fence-gate@0`, 3), scale: 2.5, heightM: 1.86, thickM: 0.2, face: '#8A5A3A', cap: '#A8704A', open: true },
};

/** One bay of wall: the sprite and the segment it lies on (world px), the wall's body, and the line it sorts by. */
export interface Piece { key: string; x1: number; y1: number; x2: number; y2: number; kind: string; zc: number; len: number; s: number; sortY: number }

export class CompoundLayer {
  readonly pieces: Piece[] = [];
  /** guard booths: where each stands, inside its gate */
  readonly booths: { x: number; y: number }[] = [];
  private metre: number;

  private constructor(private m: GameMap) {
    const t = m.tile, metre = (this.metre = t / (m.mpt || 1));
    m.walls.forEach((w, wi) => {
      const kind = m.compoundKinds[w.k] ?? 'bungalow', st = STYLES[kind] ?? STYLES.bungalow;
      const gates = m.wallGates.filter((g) => g.run === wi).map((g) => g.at * t);
      const along = cumulative(w.p), total = along[along.length - 1];
      const near = (d: number, list: number[], r: number) => list.some((v) => Math.abs(v - d) <= r);
      let bayNo = 0;
      for (let i = 0; i + 3 < w.p.length; i += 2) {
        const x1 = w.p[i], y1 = w.p[i + 1], x2 = w.p[i + 2], y2 = w.p[i + 3], segLen = Math.hypot(x2 - x1, y2 - y1);
        if (segLen < 1e-6) continue;
        const nominal = st.run.lenM * st.scale * metre, n = Math.max(1, Math.round(segLen / nominal));
        for (let j = 0; j < n; j++) {
          const a = j / n, b = (j + 1) / n, d0 = along[i / 2] + segLen * a, d1 = along[i / 2] + segLen * b, mid = (d0 + d1) / 2;
          const half = (d1 - d0) / 2;
          // a gate where the map has one; a pier where the overlay puts one, and at either end of the wall
          const isGate = near(mid, gates, half), isPier = !!st.pier && (d0 < 1 || d1 > total - 1 || near(mid, w.piers, half));
          const pc = isGate ? st.gate : isPier ? st.pier! : st.alt && st.every && bayNo % st.every === st.every - 1 ? st.alt : st.run;
          bayNo++;
          const px1 = x1 + (x2 - x1) * a, py1 = y1 + (y2 - y1) * a, px2 = x1 + (x2 - x1) * b, py2 = y1 + (y2 - y1) * b;
          this.pieces.push({ key: pc.key, x1: px1, y1: py1, x2: px2, y2: py2, kind, zc: pc.zcM, len: pc.lenM, s: st.scale, sortY: Math.max(py1, py2) });
        }
        // posts (the line kits): at every vertex of the run, once
        if (st.post) this.pieces.push({ key: st.post, x1, y1, x2: x1, y2: y1, kind, zc: 0, len: 0, s: st.scale, sortY: y1 + 0.5 });
      }
      if (st.post && w.p.length >= 4) {
        const ex = w.p[w.p.length - 2], ey = w.p[w.p.length - 1];
        this.pieces.push({ key: st.post, x1: ex, y1: ey, x2: ex, y2: ey, kind, zc: 0, len: 0, s: st.scale, sortY: ey + 0.5 });
      }
    });
    for (const gd of m.guards) { // a booth just inside the gate, on the compound's side
      const w = m.walls[gd.run];
      if (!w) continue;
      const at = pointAt(w, gd.at * t), nx = -at.dy * w.out, ny = at.dx * w.out;
      this.booths.push({ x: at.x + nx * 0.9 * t + at.dx * 0.6 * t, y: at.y + ny * 0.9 * t + at.dy * 0.6 * t });
    }
  }

  /** The layer for a map with boundary walls, else null. */
  static from(m: GameMap): CompoundLayer | null {
    return m.walls.length ? new CompoundLayer(m) : null;
  }

  /** The wall bays, posts and guard booths near the view, standing. */
  standing(vx0: number, vy0: number, vw: number, vh: number): Standing[] {
    const m = this.m, t = m.tile, pad = 2 * t, out: Standing[] = [];
    for (const p of this.pieces) {
      if (Math.max(p.x1, p.x2) < vx0 - pad || Math.min(p.x1, p.x2) > vx0 + vw + pad || p.sortY < vy0 - pad || Math.min(p.y1, p.y2) > vy0 + vh + 3 * pad) continue;
      if (!p.len) { const s = standing(p.key, p.x1, p.y1, this.metre, p.s); if (s) { s.y = p.sortY; out.push(s); } continue; }
      out.push({ y: p.sortY, draw: (g) => this.drawBay(g, p) });
    }
    for (const b of this.booths) {
      if (b.x < vx0 - pad || b.x > vx0 + vw + pad || b.y < vy0 - pad || b.y > vy0 + vh + pad) continue;
      const s = standing('police_booth', b.x, b.y, this.metre, 'structure');
      if (s) out.push(s);
    }
    return out;
  }

  /** One bay: the wall's body, then its kit sprite sheared onto the segment. */
  private drawBay(g: CanvasRenderingContext2D, p: Piece) {
    const st = STYLES[p.kind] ?? STYLES.bungalow, metre = this.metre;
    const dx = p.x2 - p.x1, dy = p.y2 - p.y1, L = Math.hypot(dx, dy), ux = dx / L, uy = dy / L;
    const up = K * st.heightM * p.s * metre, th = (st.thickM * p.s * metre) / 2;
    // the body: the cap lifted to the wall's height, and the face that looks down the screen
    const nx = -uy, ny = ux, sgn = ny >= 0 ? 1 : -1; // the face whose normal points down the screen faces the viewer
    const fx = nx * th * sgn, fy = ny * th * sgn;
    g.fillStyle = st.cap;
    quad(g, p.x1 - fx, p.y1 - fy - up, p.x2 - fx, p.y2 - fy - up, p.x2 + fx, p.y2 + fy - up, p.x1 + fx, p.y1 + fy - up);
    if (!st.open && Math.abs(ux) < 0.55) { // nearly end-on: the sprite's face would be a sliver, so the body shows it
      g.fillStyle = st.face;
      quad(g, p.x1 + fx, p.y1 + fy, p.x2 + fx, p.y2 + fy, p.x2 + fx, p.y2 + fy - up, p.x1 + fx, p.y1 + fy - up);
    }
    // the kit sprite: its base line (the wall line, zc off its origin) laid along the segment, its height up the screen
    const r = RECTS[p.key], im = atlas();
    if (!r || !im) return;
    const ppm = r.ppm || SPRITES.ppm, f = (p.s * metre) / ppm; // world px per sprite px, up the screen
    const ka = L / (p.len * ppm);                                  // and along the wall
    const u0 = r.ax - (p.len * ppm) / 2, vb = r.ay + p.zc * ppm;
    g.save();
    g.transform(ux * ka, uy * ka, 0, f, p.x1 - u0 * ux * ka, p.y1 - u0 * uy * ka - vb * f);
    g.drawImage(im, r.x, r.y, r.w, r.h, 0, 0, r.w, r.h);
    g.restore();
  }
}

function quad(g: CanvasRenderingContext2D, ...v: number[]) {
  g.beginPath(); g.moveTo(v[0], v[1]);
  for (let i = 2; i < v.length; i += 2) g.lineTo(v[i], v[i + 1]);
  g.closePath(); g.fill();
}

/** Distance along a polyline (flat x, y pairs) at each of its points. */
function cumulative(p: number[]): number[] {
  const out = [0];
  for (let i = 0; i + 3 < p.length; i += 2) out.push(out[out.length - 1] + Math.hypot(p[i + 2] - p[i], p[i + 3] - p[i + 1]));
  return out;
}

/** The point `d` px along a wall, and its direction there. */
export function pointAt(w: Wall, d: number): { x: number; y: number; dx: number; dy: number } {
  let acc = 0;
  for (let i = 0; i + 3 < w.p.length; i += 2) {
    const x1 = w.p[i], y1 = w.p[i + 1], L = Math.hypot(w.p[i + 2] - x1, w.p[i + 3] - y1);
    if (acc + L >= d || i + 4 >= w.p.length) {
      const t = L ? Math.max(0, Math.min(1, (d - acc) / L)) : 0, ux = L ? (w.p[i + 2] - x1) / L : 1, uy = L ? (w.p[i + 3] - y1) / L : 0;
      return { x: x1 + (w.p[i + 2] - x1) * t, y: y1 + (w.p[i + 3] - y1) * t, dx: ux, dy: uy };
    }
    acc += L;
  }
  return { x: w.p[0], y: w.p[1], dx: 1, dy: 0 };
}
